import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { connectRedux } from '../../helpers/connectRedux'

import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import { Page, ValidatedInput, Button, Spinner } from '../../components'
import CountryPickerModal from '../../containers/CountryPickerModal'
import LanguageSelector from '../../containers/LanguageSelector'
import countries from '../../countries.json'
import { images } from '../../images'
import { ICountry } from '../../models/country'
import { urls } from '../../routing'
import { GoogleService, LiteralsService } from '../../services'
import {
  clearSignInErrorAction,
  clearSignInFieldsAction,
  clearSignUpFieldsAction,
  resetRecoverPasswordFieldsAction,
  checkSessionAction,
  setGuestModeAction,
  setIsSigningInAction,
  setNotificationAction,
  setSignInCallingCodeAction,
  setSignInCountryAction,
  setSignInPasswordAction,
  setSignInUsernameAction,
  signInAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'
import { isPasswordValid, isTelephoneValid } from '../../helpers'
import StyledArticle from './styles'
import { appsLoginSelected, appsLogosSelected, appsStyleSelected } from '../../helpers/app'
import { AnyAction } from 'redux'

const isPhoneLogin = !!appsLoginSelected?.login.includes('phone') && !appsLoginSelected?.login.includes('email')

// Google only exists on the builds that carry a web client id, the same rule the app follows.
const showGoogleSignIn = GoogleService.isConfigured

// Google renders its own button and only takes a pixel width, capped at 400 by the library.
const GOOGLE_BUTTON_MAX_WIDTH = 400

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  language: string
  username: string
  password: string
  isSigningIn: boolean
  error: boolean
  isNewPasswordRequired: boolean
  country?: ICountry
  callingCode: string
}

class SignIn extends PureComponent<IProps> {

  private googleButton = React.createRef<HTMLDivElement>()

  constructor(props: IProps) {
    super(props)
    if (isPhoneLogin && !props.country) {
      const defaultCountry = countries.find(country => country.code === 'ES')
      if (defaultCountry) {
        props.dispatch(setSignInCountryAction(defaultCountry))
        props.dispatch(setSignInCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get isUsernameValid(): boolean {
    if (isPhoneLogin) return isTelephoneValid(this.props.username)
    return this.props.username.length > 0
  }

  private get signInAlert(): ReactNode {
    const error = this.props.error
    if (error) {
      return (
        <div className="error-container">
          <span className="error">
            { LiteralsService.get('userOrPasswordWrong', true) }
          </span>
        </div>
      )
    }
    return null
  }

  private get buttonIsDisabled(): boolean {
    const { password, isSigningIn } = this.props

    return !this.isUsernameValid || !isPasswordValid(password) || isSigningIn
  }

  private get signInButton(): ReactNode {
    const { isSigningIn } = this.props
    if (isSigningIn) return <Spinner />
    return (
      <Button
        title={ LiteralsService.get('continue') }
        onPress={ this.signIn }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  public componentDidMount(): void {
    if (!showGoogleSignIn) return

    GoogleService
      .setup(this.onGoogleCredential)
      .then(() => {
        const container = this.googleButton.current
        if (!container) return

        const width = Math.min(container.clientWidth || GOOGLE_BUTTON_MAX_WIDTH, GOOGLE_BUTTON_MAX_WIDTH)
        GoogleService.renderButton(container, width)
      })
      .catch(error => {
        console.error('Could not set up Google sign in', error)
      })
  }

  public componentDidUpdate(): void {
    const { history } = this.props
    if (this.props.isNewPasswordRequired) history.push(urls.newPasswordRequired)
  }

  public render(): ReactNode {

    const { username, password, error, country } = this.props

    return (
      <Page className={ `${appsStyleSelected?.style}` }>
        <StyledArticle>
          <div className="language-selector">
            <LanguageSelector />
          </div>
          <form onSubmit={ e => e.preventDefault() }>
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
              <h2>{ LiteralsService.get('signIn', true) }</h2>
            </header>
            { showGoogleSignIn &&
              <section className="federated">
                <div className="google-button">
                  <button type="button" className="google-pill" onClick={ this.handleGooglePress }>
                    <img src={ images['googleIcon'] } alt="" />
                    { LiteralsService.get('continueWithGoogle') }
                  </button>
                  { /* Google's own button, invisible on top: only it can hand back a credential. */ }
                  <div className="google-gsi" ref={ this.googleButton } />
                </div>
                <div className="divider">
                  { LiteralsService.get('orWithYourAccount') }
                </div>
              </section>
            }
            <section className="fields">
              {
                isPhoneLogin ? (
                  <ValidatedInput
                    name="username"
                    phone
                    label={ LiteralsService.get('emailOrUsername', true) }
                    value={ username }
                    country={ country }
                    onChange={ this.onUsernameChange }
                    error={ !this.isUsernameValid || error }
                  />
                ) : (
                  <ValidatedInput
                    name="username"
                    label={ LiteralsService.get('emailOrUsername', true) }
                    value={ username }
                    onChange={ this.onUsernameChange }
                    error={ !this.isUsernameValid || error }
                  />
                )
              }
              <ValidatedInput
                name="password"
                label={ LiteralsService.get('password', true) }
                value={ password }
                onChange={ this.onPasswordChange }
                isPassword
                error={ !isPasswordValid(password) || error }
              />
              <div className="link forgotten-password">
                <span onClick={ this.goToRecoverPassword }>
                  { LiteralsService.get('forgottenPassword', true) }
                </span>
              </div>
              { this.signInAlert }
            </section>
            { isPhoneLogin && <CountryPickerModal selectCountry={ this.onCountryChange } /> }
            <section className="actions">
              { this.signInButton }
              <div className="divider">
                { LiteralsService.get('noAccountYet') }
              </div>
              <div className="secondary-actions">
                <span onClick={ this.goToSignUp }>
                  { LiteralsService.get('createAccount').toUpperCase() }
                </span>
                <span onClick={ this.goToGuestMode }>
                  { LiteralsService.get('guestMode').toUpperCase() }
                </span>
              </div>
            </section>
          </form>
        </StyledArticle>
      </Page>
    )
  }

  private onUsernameChange = (username: string) => {
    const { error, dispatch } = this.props

    if (error) dispatch(clearSignInErrorAction())
    dispatch(setSignInUsernameAction(username))
  }

  private onPasswordChange = (password: string) => {
    const { error, dispatch } = this.props

    if (error) dispatch(clearSignInErrorAction())
    dispatch(setSignInPasswordAction(password))
  }

  private onCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setSignInCountryAction(country))
    dispatch(setSignInCallingCodeAction(country.dial_code))
  }

  private goToRecoverPassword = () => {
    const { dispatch, history } = this.props

    dispatch(resetRecoverPasswordFieldsAction())
    history.push(urls.recoverPasswordRequestCodeStep)
  }

  private signIn = () => {
    this.props.dispatch(signInAction())
  }

  // Reached only when Google's invisible button is not covering the pill (it could not render):
  // its prompt is then the way in.
  private handleGooglePress = async () => {
    const { dispatch } = this.props

    try {
      await GoogleService.prompt()
    } catch (error) {
      console.error('Google sign in is unavailable', error)
      dispatch(setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      }))
    }
  }

  // Google hands back an id_token; from there it is the app's standard session check, which the
  // gateway resolves against Google's JWKS and turns into the BoD user.
  private onGoogleCredential = () => {
    const { dispatch } = this.props

    dispatch(setIsSigningInAction())
    dispatch(checkSessionAction())
  }

  private goToSignUp = () => {
    const { dispatch, history  } = this.props

    dispatch(clearSignUpFieldsAction())
    dispatch(clearSignInFieldsAction())
    history.push(urls.signUpStep1)
  }

  private goToGuestMode = () => {
    const { dispatch, history } = this.props

    dispatch(setGuestModeAction())
    history.push(urls.newReservationBusStopsStep)
  }
}

const mapStateToProps = ({ signIn, configuration }: IStoreState) => ({
  // Redraws the literals when the language selector changes the language.
  language: configuration.language,
  username: signIn.username,
  password: signIn.password,
  isSigningIn: signIn.isSigningIn,
  error: signIn.error,
  isNewPasswordRequired: signIn.isNewPasswordRequired,
  country: signIn.country,
  callingCode: signIn.callingCode
})

export default withRouter(connectRedux(mapStateToProps, SignIn))
